import { Alert, Box, Chip, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagCountry from 'src/components/common/FlagCountry';

// Component
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import Table from 'src/components/Table';
import { renderNumberOrDefault, renderTextOrDefault, renderValueNumberTrade } from 'src/utils/helpers/stringHelpers';

import { IListButtonItem } from 'src/components/common/ButtonGroupFilter';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryListBuyerByPort,
  useQueryListSupplierByPort,
  useQueryPortOrigin,
  useQueryPortTransactionProportion,
} from 'src/services/queries/useQueryTradeData';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { TypeQueryParamPortTransaction } from 'src/utils/typings/dto/tradeData';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

import dayjs from 'dayjs';
import { every, get, isArray, isEmpty, map, replace } from 'lodash';
import numeral from 'numeral';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import ExportDataTable from 'src/components/ExportData';
import { URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import BarChart from 'src/components/HighChart/Barchart';
import { BarChartPrimaryColor } from 'src/components/HighChart/Barchart/config';
import TreemapChart from 'src/components/HighChart/Treemap';
import { TREEMAP_PALLET_COLOR } from 'src/components/HighChart/Treemap/config';
import LinkToCompanyDetail from 'src/components/LinkToCompanyDetail';
import ModalComponent from 'src/components/Modal/ModalComponent';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import FilterChart from 'src/features/TradeData/Common/FilterChart';
import { TradeDataUnitByFilter } from 'src/features/TradeData/Common/TradeDataUnitbyFilter';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import { formatValueUSD } from 'src/utils/helpers/utilities';

const ViewByPorts = ({ selectedTab }: { selectedTab: TypeTabTradeData }) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const tradeDataUnitByFilter = useMemo(() => new TradeDataUnitByFilter(), []);

  const [unitFilter, setUnitFilter] = useState<KeyFilterChartTradeDataUnitBy>(tradeDataUnitByFilter.value);
  const { checkRestriction } = useProvisionRestriction();

  const [openBuyerList, setOpenBuyerList] = useState<boolean>(false);
  const { setSort, pooSortParams, podSortParams } = useTradeDataSortStore();
  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const theme = useTheme();
  const { setSort: _, pooPopupTableSortParams, podPopupTableSortParams } = useTradeDataSortStore();

  const isPortOfOrigin = selectedTab === TypeTabTradeData.PortOrigin;

  const tableSortParams = isPortOfOrigin ? pooSortParams : podSortParams;
  const popupTableSortParams = isPortOfOrigin ? pooPopupTableSortParams : podPopupTableSortParams;
  const tableSortParamsName = isPortOfOrigin ? 'pooSortParams' : 'podSortParams';
  const popupTableSortParamsName = isPortOfOrigin ? 'pooPopupTableSortParams' : 'podPopupTableSortParams';

  const [proportionTablePaging, setProportionTablePaging] = useState({
    page: 0,
    page_size: 10,
  });

  const [popupTablePaging, setPopupTablePaging] = useState({
    page: 0,
    page_size: 10,
  });

  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });

  useEffect(() => {
    setProportionTablePaging({
      page: 0,
      page_size: 10,
    });
  }, [selectedTab]);

  const proportionChartRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...queryParams,
      limit: 10,
      offset: 0,
      is_exp_port: isPortOfOrigin,
      sort_by: unitFilter,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      order: 'desc',
    };
  }, [queryParams, isPortOfOrigin, unitFilter, selectedRangeTime]);

  const proportionTableRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...queryParams,
      limit: proportionTablePaging.page_size,
      offset: proportionTablePaging.page,
      is_exp_port: isPortOfOrigin,
      sort_by: tableSortParams?.sortBy,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      order: tableSortParams?.sort,
    };
  }, [queryParams, isPortOfOrigin, tableSortParams, selectedRangeTime, proportionTablePaging]);

  const popupTableRequestPayload = useMemo(() => {
    return {
      ...queryParams,
      limit: popupTablePaging.page_size,
      offset: popupTablePaging.page,
      is_exp_port: isPortOfOrigin,
      sort_by: popupTableSortParams?.sortBy,
      order: popupTableSortParams?.sort,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
    };
  }, [queryParams, isPortOfOrigin, selectedRangeTime, popupTableSortParams, popupTablePaging]);
  const [selectedRecord, setSelectedRecord] = useState({
    is_buyer: false,
    record: undefined,
  } as {
    is_buyer: boolean;
    record: ITradeDataByProportionalPort | undefined;
  });

  useEffect(() => {
    if (!openBuyerList) {
      setPopupTablePaging({
        page: 0,
        page_size: 10,
      });
    }
  }, [openBuyerList]);

  const endpointExportTablePopup = useMemo(() => {
    const mappingEndpointExport = {
      portOfOrigin: {
        buyer: URL_ENDPOINT.tradeData.exportPortOriginBuyer,
        supplier: URL_ENDPOINT.tradeData.exportPortOriginSupplier,
      },
      portOfDelivery: {
        buyer: URL_ENDPOINT.tradeData.exportPortDeliveryBuyer,
        supplier: URL_ENDPOINT.tradeData.exportPortDeliverySupplier,
      },
    };
    return get(
      mappingEndpointExport,
      `${isPortOfOrigin ? 'portOfOrigin' : 'portOfDelivery'}.${selectedRecord.is_buyer ? 'buyer' : 'supplier'}`,
    );
  }, [selectedRecord.is_buyer, isPortOfOrigin]);

  // const transactionsPaymentStatusResp = useQueryTransactionsPaymentStatus(proportionChartRequestPayload);

  const getData = () => {
    let sortField = pooPopupTableSortParams?.sortBy;
    if (pooPopupTableSortParams?.sortBy === 'company_name') {
      sortField = selectedRecord.is_buyer ? 'buyer_name' : 'supplier_name';
    }

    if (selectedRecord.is_buyer) {
      return useQueryListBuyerByPort({
        port_locode: selectedRecord.record?.port_locode || '',
        params: { ...popupTableRequestPayload, sort_by: sortField },
      });
    } else {
      return useQueryListSupplierByPort({
        port_locode: selectedRecord.record?.port_locode || '',
        params: { ...popupTableRequestPayload, sort_by: sortField },
      });
    }
  };

  const {
    data: proportionalTableData,
    isFetching: proportionalTableIsFetching,
    dataMapping: proportionalTableDataMapping,
  } = useQueryPortOrigin(acceptCreditPayment ? proportionTableRequestPayload : undefined);
  const { data: popupTableData, isFetching: popupTableDataIsFetching, dataMapping: popupTableDataMapping } = getData();

  const handleCloseBuyerList = () => {
    setSelectedRecord({
      is_buyer: false,
      record: undefined,
    });
    setOpenBuyerList(false);
  };
  const [shouldShowBillableDataLoading, hasEnoughCredit] = useCreditFlow({
    response: proportionalTableData,
    featureInfo: {
      featureId: LIST_FEATURES.TRADE_DATA_BILL_OF_LADING,
      action: LIST_ACTIONS.VIEW,
    },
    // paymentStatus: transactionsPaymentStatusResp.data?.data?.bol,
    // isCollectingData: transactionsPaymentStatusResp.isFetching,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const [shouldShowLoadingPopup] = useCreditFlow({
    response: popupTableData,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
    cbClose: () => handleCloseBuyerList(),
  });

  const proportionTableConfigColumn: ConfigCell<ITradeDataByProportionalPort>[] = [
    {
      disablePadding: false,
      id: 'port_name',
      disableSort: true,
      label: isPortOfOrigin
        ? t('trade_data.port_origin.table.port_name')
        : t('trade_data.port_delivery.table.port_name'),
      cell: (data: ITradeDataByProportionalPort) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={data?.country_code || ''} />
            <Typography variant="body2" color="text.secondary">
              {renderTextOrDefault(data.port_name)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.shipments)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.teu)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {formatValueUSD(data.value_usd)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'suppliers',
      label: t('trade_data.port_origin.table.total_suppliers'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography
          onClick={() => {
            setSelectedRecord({
              is_buyer: false,
              record: data,
            });
            setOpenBuyerList(true);
          }}
          variant="body2"
          color="secondary"
        >
          {renderNumberOrDefault(data.total_suppliers)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'buyers',
      label: t('trade_data.port_origin.table.total_buyers'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography
          variant="body2"
          color="secondary"
          onClick={() => {
            setSelectedRecord({
              is_buyer: true,
              record: data,
            });
            setOpenBuyerList(true);
          }}
        >
          {renderNumberOrDefault(data.total_buyers)}
        </Typography>
      ),
    },
  ];

  const {
    isFetching: isFetchingChart,
    data: { data: chartData },
  } = useQueryPortTransactionProportion(acceptCreditPayment ? proportionChartRequestPayload : undefined);

  const checkIsEmptyDataAndAutoChangeUnit = () => {
    if (isFetchingChart || tradeDataUnitByFilter.isUserClickBack(unitFilter as KeyFilterChartTradeDataUnitBy)) return;
    const isEmptyData = isEmpty(chartData) || every(chartData, (item) => get(item, unitFilter) === 0);
    if (isEmptyData) {
      setUnitFilter(tradeDataUnitByFilter.next());
    }
  };

  useEffect(() => {
    checkIsEmptyDataAndAutoChangeUnit();
  }, [chartData, isFetchingChart]);

  const renderTitlePopup = () => {
    switch (selectedRecord.is_buyer) {
      case true:
        return t('trade_data.port_origin.table.total_buyers');
      case false:
        return t('trade_data.port_origin.table.total_suppliers');
    }
  };

  const renderChart = () => {
    if (isEmpty(chartData) && !isFetchingChart && !shouldShowBillableDataLoading) {
      return (
        <CompanyInfoNoData
          title={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
          content={
            <>
              Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
              logistics.
            </>
          }
          sxContainer={{
            borderWidth: 0,
          }}
        />
      );
    }
    return (
      <TreemapChart
        colors={TREEMAP_PALLET_COLOR}
        height={600}
        loading={shouldShowBillableDataLoading || isFetchingChart}
        tooltipFormatter={function () {
          const point: Highcharts.Point = this.point;
          const transactionDetail = chartData?.find(({ port_locode }) => port_locode === point?.name);
          const countryCode = transactionDetail?.country_code;
          const countryName = transactionDetail?.port_name;
          const value = get(transactionDetail, `${unitFilter}`, 0);
          const valuePercent = get(transactionDetail, `percentage_${unitFilter}`, 0);
          const flag = getEmojiFlag(countryCode || '');
          return `
            <div style="min-width: 270px">
              <span style="font-size: 24px; color: inherit; display: block; color: inherit; fontWeight: 600; margin-bottom: 5px">
                ${valuePercent}%
              </span>
              <span style="color: inherit; display: block; color: inherit; fontWeight: 600">
              ${flag} ${countryName || t('global.others')}
              </span>
              <span style="color: inherit; display: block; color: ${theme.palette.text.secondary}">${t('trade_data.filter_chart.unit.' + unitFilter)} <b style="color: #fff">
              ${renderNumberOrDefault(value)}</b>
              </span>
            </div>`;
        }}
        dataLabelFormatter={function () {
          const point: Highcharts.Point = this.point;
          const transactionDetail = chartData?.find(({ port_locode }) => port_locode === point?.name);
          const countryCode = transactionDetail?.country_code;
          const countryName = transactionDetail?.port_name;
          const valuePercent = get(transactionDetail, `percentage_${unitFilter}`, 0);
          const flag = getEmojiFlag(countryCode || '');
          return `<div style="font-size: 12px; font-weight: 500; color: ${theme.palette.secondary.main}; position: relative">
          <p style="display: absolute; top:0; left: 0;">${flag} ${countryName}</p>
          <br/>
          <p style="display: block; font-weight: 900; font-size: 20px">${valuePercent}%</p>
            </div>`;
        }}
        data={getArrayDefault(chartData).map((transaction, index) => ({
          name: transaction.port_locode,
          value: Math.cbrt(Number(get(transaction, unitFilter, ''))),
          sortIndex: index,
        }))}
      />
    );
  };

  const popupTableConfigColumn: ConfigCell<IBuyerInfo>[] = [
    {
      disablePadding: false,
      id: 'company_name',
      label: t('trade_data.port_origin.table.company_name'),
      cell: (data: IBuyerInfo) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={data?.country_code || ''} />
            <LinkToCompanyDetail
              id={data?.id || ''}
              companyName={data.company_name || ''}
              params={queryParams}
              viewedAsType={selectedRecord.is_buyer ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier}
            />
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: IBuyerInfo) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.shipments)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: IBuyerInfo) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      cell: (data: IBuyerInfo) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.teu)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: IBuyerInfo) => {
        const checkRestrictionValue = checkRestriction({
          countryCode: data?.country_code || '',
          typeData: LIST_RESTRICTION_DATA.VALUES_USD,
        });
        return (
          <Typography variant="body2" color="text.secondary">
            <Typography variant="body2" color="text.secondary">
              {formatValueUSD(data.value_usd)}
            </Typography>
            {checkRestrictionValue && <TooltipTradeDataLimit />}
          </Typography>
        );
      },
    },
  ];

  if (
    !proportionalTableIsFetching &&
    !isFetchingChart &&
    isEmpty(getArrayDefault(proportionalTableDataMapping)) &&
    isEmpty(getArrayDefault(chartData))
  ) {
    return (
      <CompanyInfoNoData
        title={hasEnoughCredit ? t('trade_data.common.notice_no_data.title') : 'Not Enough Credit'}
        content={
          <>
            {hasEnoughCredit
              ? t('trade_data.common.notice_no_data.sub_title')
              : 'Can not access the data, please upgrade your plan'}
          </>
        }
      />
    );
  }

  const barChart = useMemo(() => {
    const sortedTransactions = getArrayDefault(isArray(chartData) ? [...chartData] : []).sort(
      (a, b) => get(b, unitFilter, 0) - get(a, unitFilter, 0),
    );
    const data = map(sortedTransactions, (transaction) => get(transaction, unitFilter) ?? 0);
    const xAxis = map(
      sortedTransactions,
      ({ port_name, country_code }) => `${getEmojiFlag(country_code)} ${port_name}`,
    );
    return (
      <BarChart
        tooltipFormatter={function () {
          const point: Highcharts.Point = this.point;
          const flag = point.category;
          const value = numeral(point.options.y).format('0,0');
          return `<div style="font-size: 14px; font-weight: 500">
          <p style="display: block">${flag}</p>
          <p style="display: block">${value}</p>
            </div>`;
        }}
        yAxis={{
          type: 'logarithmic',
          minorTickInterval: 'auto',
        }}
        xAxis={{
          categories: xAxis,
        }}
        series={[
          {
            data: data,
            type: 'column',
            showInLegend: false,
            borderRadius: 8,
            color: BarChartPrimaryColor,
            dataLabels: {
              rotation: 0,
              verticalAlign: 'top',
              y: -20,
              align: 'left',
              enabled: true,
              color: theme.palette.text.primary,
            },
          },
        ]}
        loading={isFetchingChart}
        height={600}
      />
    );
  }, [unitFilter, JSON.stringify(chartData)]);

  return (
    <Stack direction="column" spacing={3}>
      <WhiteBoxContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FilterChart
              onSelectTimeRange={({ fromTime, toTime }) => {
                setSelectedRangeTime({
                  fromTime: fromTime,
                  toTime: toTime,
                });
              }}
              title={
                <Stack direction={'row'} gap={1} alignItems={'center'}>
                  <Typography variant="h5">
                    {isPortOfOrigin
                      ? t('trade_data.port_origin.title_chart')
                      : t('trade_data.port_delivery.title_chart')}
                  </Typography>
                  <Chip
                    variant="outlined"
                    color="info"
                    label={
                      <Typography fontSize={8} fontWeight={800} color={'inherit'}>
                        BETA
                      </Typography>
                    }
                    size="small"
                  />
                </Stack>
              }
              onSelectUnitBy={(unit: IListButtonItem['key']) => {
                setSort(tableSortParamsName, {
                  sortBy: unit,
                  sort: 'desc',
                });
                setUnitFilter(unit as KeyFilterChartTradeDataUnitBy);
              }}
              selectedUnitBy={unitFilter}
              selectedRangeTime={{
                fromTime: proportionChartRequestPayload.start_date || '',
                toTime: proportionChartRequestPayload.end_date || '',
              }}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            {renderChart()}
          </Grid>

          <Grid item xs={12} lg={6}>
            {barChart}
          </Grid>
        </Grid>
      </WhiteBoxContent>

      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <Typography display={'flex'} justifyContent={'space-between'} textAlign="left" variant="h5">
            {isPortOfOrigin ? t('trade_data.port_origin.title_table') : t('trade_data.port_delivery.title_table')}{' '}
            <ExportDataTable
              fileName={isPortOfOrigin ? 'port-of-origin' : 'port-of-delivery'}
              paramsExport={proportionTableRequestPayload}
              featureId={
                isPortOfOrigin ? LIST_FEATURES.TRADE_DATA_PORT_OF_ORIGIN : LIST_FEATURES.TRADE_DATA_PORT_OF_DELIVERY
              }
              endpoint={
                isPortOfOrigin
                  ? get(URL_EXPORT_MAPPING, LIST_FEATURES.TRADE_DATA_PORT_OF_ORIGIN)
                  : get(URL_EXPORT_MAPPING, LIST_FEATURES.TRADE_DATA_PORT_OF_DELIVERY)
              }
              totalRecordsAvailable={proportionalTableData?.total_records || 0}
            />
          </Typography>
          <Table
            handleSort={(data) => {
              setSort(tableSortParamsName, data);
            }}
            tableMaxHeight={600}
            onChangePage={(value) => {
              setProportionTablePaging({ ...proportionTablePaging, page: value });
            }}
            onChangePageSize={(value) => setProportionTablePaging({ page: 0, page_size: value })}
            loading={shouldShowBillableDataLoading || proportionalTableIsFetching}
            page={proportionalTableData.page}
            pageSize={proportionalTableData.page_size}
            totalRecords={proportionalTableData.total_records}
            dataKey={'id'}
            data={proportionalTableDataMapping}
            configColumn={proportionTableConfigColumn}
          />
        </Stack>
      </WhiteBoxContent>

      <ModalComponent padding={0} pt={0} open={openBuyerList} handleClose={handleCloseBuyerList}>
        <Stack direction="column" spacing={3} width="80vw" bgcolor={theme.palette.background.paper} p={4}>
          <Stack alignItems={'center'} direction="row" justifyContent="space-between">
            <Typography variant="h5">{renderTitlePopup()}</Typography>
            {queryParams.hs_code ? (
              <Typography variant="body2" color="text.secondary">
                This is a list of {selectedRecord.is_buyer ? 'buyers' : 'suppliers'} with HS code{' '}
                {popupTableRequestPayload.hs_code} from{' '}
                {dayjs(popupTableRequestPayload.start_date).format(FORMAT_DATE_TIME.MMM_DD_YYYY)} until{' '}
                {dayjs(popupTableRequestPayload.end_date).format(FORMAT_DATE_TIME.MMM_DD_YYYY)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                This is a list of {selectedRecord.is_buyer ? 'buyers' : 'suppliers'} with production description{' '}
                <b>{popupTableRequestPayload.keyword}</b> from{' '}
                {dayjs(popupTableRequestPayload.start_date).format(FORMAT_DATE_TIME.MMM_DD_YYYY)} until{' '}
                {dayjs(popupTableRequestPayload.end_date).format(FORMAT_DATE_TIME.MMM_DD_YYYY)}
              </Typography>
            )}
            <ExportDataTable
              paramsExport={popupTableRequestPayload}
              fileName={isPortOfOrigin ? 'port-of-origin' : 'port-of-delivery'}
              featureId={LIST_FEATURES.INTERNAL_TRADE_DATA_SUB_LIST_SUPPLIERS_BUYERS}
              endpoint={replace(endpointExportTablePopup, ':id', selectedRecord.record?.port_locode ?? '')}
              totalRecordsAvailable={popupTableData?.total_records || 0}
            />
          </Stack>

          {isEmpty(popupTableDataMapping) && !popupTableDataIsFetching ? (
            <Alert severity="info" color="info">
              Withheld by Government
            </Alert>
          ) : (
            <Table
              maxRow={10}
              handleSort={(popupTableData) => {
                setSort(popupTableSortParamsName, popupTableData);
              }}
              onChangePage={(value) => {
                setPopupTablePaging({ ...popupTablePaging, page: value });
              }}
              onChangePageSize={(value) => setPopupTablePaging({ page: 0, page_size: value })}
              loading={shouldShowLoadingPopup || popupTableDataIsFetching}
              page={popupTableData.page}
              pageSize={popupTableData.page_size}
              totalRecords={popupTableData.total_records}
              dataKey={'id'}
              data={popupTableDataMapping}
              configColumn={popupTableConfigColumn}
              pagingTooltipsText={
                (selectedRecord?.record?.[selectedRecord?.is_buyer ? 'total_buyers' : 'total_suppliers'] || 0) >
                (popupTableData?.total_records || 9999999)
                  ? 'trade_data.count_mismatch.tooltip'
                  : undefined
              }
            />
          )}
        </Stack>
      </ModalComponent>
    </Stack>
  );
};

export default ViewByPorts;
